import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectCreditsByBank, selectTotalMonthlyDebt, selectBanks, selectMembers } from '@/utils/store';
import { percentage } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui';
import PageHeader from '@/components/shared/PageHeader';
import AddBankModal from '@/components/shared/AddBankModal';
import AddCreditModal from '@/components/shared/AddCreditModal';
import PayInstallmentModal from '@/components/shared/PayInstallmentModal';
import { Plus, CreditCard, Landmark, Building2, Pencil, Trash2, Copy } from 'lucide-react';

export default function Credits() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const banks = useStore(selectBanks);
  const creditsByBank = useStore(selectCreditsByBank);
  const monthlyDebt = useStore(selectTotalMonthlyDebt);

  const members = useStore(selectMembers);
  const removeBank = useStore((s) => s.removeBank);
  const duplicateBank = useStore((s) => s.duplicateBank);
  const removeCredit = useStore((s) => s.removeCredit);
  const duplicateCredit = useStore((s) => s.duplicateCredit);

  const [showBankModal, setShowBankModal] = useState(false);
  const [editBank, setEditBank] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [editCredit, setEditCredit] = useState(null);
  const [creditModalBankId, setCreditModalBankId] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);

  const handleAddCredit = (bankId) => {
    setCreditModalBankId(bankId);
    setShowCreditModal(true);
  };

  const handlePayInstallment = (credit) => {
    setSelectedCredit(credit);
    setShowPayModal(true);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('credits.title')}
        hint={t('common.hints.credits')}
        action={
          <Button size="sm" icon={Plus} onClick={() => setShowBankModal(true)}>
            {t('credits.addBank')}
          </Button>
        }
      />

      {/* Monthly debt summary */}
      <div className="card bg-gradient-to-br from-violet-500 to-purple-600 text-white border-0">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-1">
          <CreditCard size={16} />
          {t('credits.monthlyDebt')}
        </div>
        <div className="text-3xl font-bold">{formatCurrency(monthlyDebt)}</div>
      </div>

      {/* Empty state */}
      {banks.length === 0 && (
        <div className="card text-center py-12">
          <Building2 size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-sm text-gray-500">{t('credits.noBanks')}</p>
        </div>
      )}

      {/* Credits by bank */}
      {Object.values(creditsByBank).map(({ bank, credits }) => {
        const bankMembers = bank.memberIds?.length > 0 ? bank.memberIds.map((id) => members.find((m) => m.id === id)).filter(Boolean) : [];
        return (
        <div key={bank.id} className="space-y-3">
          {/* Bank header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: bank.color }} />
              <h2 className="font-semibold text-gray-900 dark:text-white">{bank.name}</h2>
              {bankMembers.map((m) => (
                m.emoji ? (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                    style={{ backgroundColor: m.color + '20', color: m.color }}
                  >
                    {m.emoji} {m.name}
                  </span>
                ) : (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                    style={{ backgroundColor: m.color }}
                  >
                    {m.initials}
                  </span>
                )
              ))}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => duplicateBank(bank.id)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Copy size={16} className="text-gray-400" />
              </button>
              <button
                onClick={() => { setEditBank(bank); setShowBankModal(true); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Pencil size={16} className="text-gray-400" />
              </button>
              <button
                onClick={() => removeBank(bank.id)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Trash2 size={16} className="text-gray-400" />
              </button>
              <button
                onClick={() => handleAddCredit(bank.id)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Plus size={18} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Credit list */}
          {credits.length === 0 && (
            <div className="card py-6 text-center">
              <p className="text-sm text-gray-400">{t('credits.noCredits')}</p>
            </div>
          )}

          {credits.map((credit) => {
            const isCompleted = credit.paidInstallments >= credit.totalInstallments;
            const pct = percentage(credit.paidInstallments, credit.totalInstallments);

            return (
              <div key={credit.id} className="card space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {credit.description}
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        credit.type === 'card_installment'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                          : credit.type === 'consumer_loan'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                            : credit.type === 'mortgage'
                              ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400'
                      }`}>
                        {t(`credits.types.${credit.type}`)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {t('credits.installmentOf', {
                        current: Math.min(credit.paidInstallments + 1, credit.totalInstallments),
                        total: credit.totalInstallments,
                      })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white">
                      {formatCurrency(credit.installmentAmount)}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {formatCurrency(credit.totalAmount)}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${isCompleted ? 'bg-success-500' : 'bg-brand-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-gray-400">
                      {credit.paidInstallments}/{credit.totalInstallments}
                    </span>
                    <span className="text-[10px] text-gray-400">{pct}%</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => duplicateCredit(credit.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Copy size={14} className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => { setEditCredit(credit); setShowCreditModal(true); }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Pencil size={14} className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => removeCredit(credit.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Trash2 size={14} className="text-gray-400" />
                  </button>
                  <div className="flex-1">
                    {isCompleted ? (
                      <div className="text-center">
                        <span className="text-xs font-semibold text-success-500 bg-success-50 dark:bg-success-500/10 px-3 py-1 rounded-full">
                          {t('credits.completed')}
                        </span>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full"
                        onClick={() => handlePayInstallment(credit)}
                      >
                        {t('credits.payInstallment')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        );
      })}

      <AddBankModal
        open={showBankModal}
        onClose={() => { setShowBankModal(false); setEditBank(null); }}
        bank={editBank}
      />
      <AddCreditModal
        open={showCreditModal}
        onClose={() => { setShowCreditModal(false); setCreditModalBankId(null); setEditCredit(null); }}
        defaultBankId={creditModalBankId}
        credit={editCredit}
      />
      <PayInstallmentModal
        open={showPayModal}
        onClose={() => { setShowPayModal(false); setSelectedCredit(null); }}
        credit={selectedCredit}
      />
    </div>
  );
}
